import { DocumentData, GeoPoint, QueryDocumentSnapshot, Timestamp } from "firebase/firestore";
import { isPOINullMetaType, POI, type POIType, Position, TenantApp } from "../../types";
import { BleDevice, TenantAppFirestoreDoc } from "../../types/tenant.types";

// Convert Firestore Tenant types to application types
export const convertAppTenantSnapToAppTenant = (
    tenantAppData: TenantAppFirestoreDoc,
    POIs: QueryDocumentSnapshot<DocumentData, DocumentData>[],
    ble: QueryDocumentSnapshot<DocumentData, DocumentData>[]): TenantApp => {

    const poisList: POI[] = POIs.map(poiDoc => {
        const raw = poiDoc.data() as any;
        const type = raw.type as POIType;

        const meta = isPOINullMetaType(type) ? null : normalizePOIMeta(raw.meta);

        return {
            ...raw,
            meta,
            id: poiDoc.id
        } as POI;
    });

    const bleList = ble.map(bleDoc => {
        const bleData = bleDoc.data() as BleDevice;
        return {
            identifier: bleData.identifier,
            busy: bleData.busy
        };
    });

    const tenant: TenantApp = {
        ...tenantAppData,
        pois: poisList,
        ble: bleList,
        createdAt: ensureMillis(tenantAppData.createdAt),
        updatedAt: ensureMillis(tenantAppData.updatedAt),
    };

    return tenant
}

const normalizePOIMeta = (meta: any): any => {
    if (!meta || typeof meta !== 'object') return meta;

    if (meta.updatedAt !== undefined && meta.updatedAt !== null) {
        return {
            ...meta,
            updatedAt: ensureMillis(meta.updatedAt)
        };
    }

    return meta;
}


/**
  * Converts Firestore GeoPoint to application Position format
  * @param geoPoint Firestore GeoPoint
  * @returns Position object with lt (latitude) and lg (longitude)
  */
export const convertGeoPointToPosition = (geoPoint: GeoPoint): Position => {
    return {
        lt: geoPoint.latitude,
        lg: geoPoint.longitude,
    };
}

/**
 * Converte il tipo Position in Firestore GeoPoint
 * @param position Position object with lt and lg
 * @returns Firestore GeoPoint
 */
export const convertPositionToGeoPoint = (position: Position): GeoPoint => {
    return new GeoPoint(position.lt, position.lg);
}

/**
 * Rimuove valori undefined da un oggetto in modo ricorsivo
 * @param obj Oggetto da pulire
 * @returns Nuovo oggetto senza campi undefined
 */
export const removeUndefinedFields = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => removeUndefinedFields(item));
    }

    if (typeof obj === 'object') {
        const cleaned: any = {};
        for (const key in obj) {
            if (obj[key] !== undefined) {
                cleaned[key] = removeUndefinedFields(obj[key]);
            }
        }
        return cleaned;
    }

    return obj;
}

/**
 * Estrae le coordinate da un oggetto Point di Firestore
 * @params point Oggetto Point di Firestore
 * @returns Oggetto Position con lt e lg
 */
export const extractPoint = (point: { _lat: number, _long: number }): Position => ({ lt: point._lat, lg: point._long });

/**
 * Estrae il timestamp in millisecondi da un oggetto Firestore Timestamp o simile
 * @params date Oggetto data di Firestore
 * @returns Timestamp in millisecondi
 */
export const ensureMillis = (date: any): number => {
    if (!date) return 0;
    if (typeof date.toMillis === 'function') return date.toMillis();
    if (date.seconds !== undefined) return date.seconds * 1000;
    return new Date(date).getTime();
};